import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  serverTimestamp,
  Timestamp,
  arrayUnion,
  arrayRemove,
  orderBy,
  limit,
  setDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { 
  Organization, 
  OrganizationFormData, 
  OrganizationMember, 
  OrganizationRole,
  Ticket,
  TicketType
} from '../types/Organization';
import { v4 as uuidv4 } from 'uuid';
import { Event } from '../types/Event';

/**
 * Create a new organization
 */
export const createOrganization = async (
  userId: string,
  data: OrganizationFormData
): Promise<string> => {
  try {
    // Create organization document
    const orgData: Omit<Organization, 'id'> = {
      name: data.name,
      description: data.description || '',
      logoUrl: '',
      coverUrl: '',
      website: data.website || '',
      email: data.email || '',
      phone: data.phone || '',
      address: data.address || '',
      creatorId: userId,
      createdAt: serverTimestamp(), // Works with Date | any in interface
      updatedAt: serverTimestamp(), // Works with Date | any in interface
      members: [
        {
          id: uuidv4(),
          userId: userId,
          organizationId: '', // Will be set after document is created
          email: data.email || '',
          name: 'Organization Owner',
          displayName: 'Organization Owner',
          role: 'owner',
          status: 'active',
          invitedAt: new Date(),
          invitedBy: userId,
          joinedAt: new Date()
        }
      ],
      status: 'active',
      isPublic: data.isPublic || false,
      social: data.social || {}
    };

    // Add the organization to Firestore
    const docRef = await addDoc(collection(db, 'organizations'), orgData);
    
    // Handle logo and cover photo uploads if provided
    const updates: Record<string, any> = {};
    
    if (data.logoImage) {
      const logoUrl = await uploadOrganizationImage(docRef.id, data.logoImage, 'logo');
      updates.logoUrl = logoUrl;
    }
    
    if (data.coverImage) {
      const coverUrl = await uploadOrganizationImage(docRef.id, data.coverImage, 'cover');
      updates.coverPhotoUrl = coverUrl;
    }
    
    // Apply updates if needed
    if (Object.keys(updates).length > 0) {
      await updateDoc(doc(db, 'organizations', docRef.id), updates);
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error;
  }
};

/**
 * Get organization by ID
 */
export const getOrganization = async (orgId: string): Promise<Organization | null> => {
  try {
    const orgDoc = await getDoc(doc(db, 'organizations', orgId));
    
    if (!orgDoc.exists()) {
      return null;
    }
    
    const orgData = orgDoc.data();
    return { id: orgDoc.id, ...orgData } as Organization;
  } catch (error) {
    console.error('Error getting organization:', error);
    throw error;
  }
};

/**
 * Update organization details
 */
export const updateOrganization = async (
  orgId: string,
  data: Partial<Organization>
): Promise<void> => {
  try {
    const updateData = {
      ...data,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(doc(db, 'organizations', orgId), updateData);
  } catch (error) {
    console.error('Error updating organization:', error);
    throw error;
  }
};

/**
 * Delete an organization
 */
export const deleteOrganization = async (orgId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, 'organizations', orgId));
  } catch (error) {
    console.error('Error deleting organization:', error);
    throw error;
  }
};

/**
 * Get organizations where user is a member
 */
export const getUserOrganizations = async (userId: string): Promise<Organization[]> => {
  try {
    // Get organizations where user is the creator
    const ownedOrgsQuery = query(
      collection(db, 'organizations'),
      where('creatorId', '==', userId)
    );
    
    // Get organizations where user is a member
    const memberOrgsQuery = query(
      collection(db, 'organizations'),
      where('members', 'array-contains', { userId: userId })
    );
    
    const [ownedSnapshot, memberSnapshot] = await Promise.all([
      getDocs(ownedOrgsQuery),
      getDocs(memberOrgsQuery)
    ]);
    
    const ownedOrgs: Organization[] = [];
    ownedSnapshot.forEach(doc => {
      ownedOrgs.push({ id: doc.id, ...doc.data() } as Organization);
    });
    
    const memberOrgs: Organization[] = [];
    memberSnapshot.forEach(doc => {
      // Avoid duplicates (if user is both creator and listed in members)
      if (!ownedOrgs.some(org => org.id === doc.id)) {
        memberOrgs.push({ id: doc.id, ...doc.data() } as Organization);
      }
    });
    
    return [...ownedOrgs, ...memberOrgs];
  } catch (error) {
    console.error('Error getting user organizations:', error);
    throw error;
  }
};

/**
 * Upload organization images (logo/cover)
 */
export const uploadOrganizationImage = async (
  orgId: string,
  file: File,
  type: 'logo' | 'cover'
): Promise<string> => {
  try {
    const fileExtension = file.name.split('.').pop();
    const filename = `organizations/${orgId}/${type}-${Date.now()}.${fileExtension}`;
    const storageRef = ref(storage, filename);
    
    // Upload the file
    await uploadBytes(storageRef, file);
    
    // Get the download URL
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  } catch (error) {
    console.error(`Error uploading organization ${type}:`, error);
    throw error;
  }
};

/**
 * Invite member to organization
 */
export const inviteMemberToOrganization = async (
  organizationId: string,
  inviterUserId: string,
  email: string,
  role: OrganizationRole = 'member',
  displayName?: string,
  photoURL?: string
): Promise<string> => {
  try {
    // Normalize email to lowercase
    email = email.toLowerCase();
    
    // Check if organization exists
    const orgDoc = await getDoc(doc(db, 'organizations', organizationId));
    if (!orgDoc.exists()) {
      throw new Error('Organization not found');
    }
    
    // Check if user is already a member
    const orgData = orgDoc.data() as Organization;
    const existingMember = orgData.members.find(m => m.email === email);
    
    if (existingMember) {
      throw new Error('User is already a member of this organization');
    }
    
    // Generate invite ID
    const inviteId = uuidv4();
    
    // Create member reference
    const memberData: OrganizationMember = {
      id: inviteId,
      userId: '', // Will be filled when user accepts
      organizationId,
      email,
      name: displayName || email.split('@')[0],
      displayName: displayName || email.split('@')[0],
      avatarUrl: photoURL,
      photoURL,
      role,
      status: 'invited', // Using 'invited' instead of 'pending'
      invitedAt: new Date(),
      invitedBy: inviterUserId,
      joinedAt: new Date()
    };
    
    // Update organization with new member
    await updateDoc(doc(db, 'organizations', organizationId), {
      members: arrayUnion(memberData),
      updatedAt: serverTimestamp()
    });
    
    // Create invitation document for tracking
    await setDoc(doc(db, 'organizationInvitations', inviteId), {
      organizationId,
      inviterId: inviterUserId,
      email,
      role,
      status: 'pending',
      createdAt: serverTimestamp(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
    });
    
    return inviteId;
  } catch (error) {
    console.error('Error inviting member to organization:', error);
    throw error;
  }
};

/**
 * Remove member from organization
 */
export const removeOrganizationMember = async (
  orgId: string,
  memberUserId: string
): Promise<void> => {
  try {
    // Get organization data
    const orgDoc = await getDoc(doc(db, 'organizations', orgId));
    
    if (!orgDoc.exists()) {
      throw new Error('Organization not found');
    }
    
    // Get current members
    const orgData = orgDoc.data();
    const members = orgData.members || [];
    
    // Find the member to remove
    const memberIndex = members.findIndex((m: OrganizationMember) => m.userId === memberUserId);
    
    if (memberIndex === -1) {
      throw new Error('Member not found in organization');
    }
    
    // Cannot remove the organization creator
    if (orgData.creatorId === memberUserId) {
      throw new Error('Cannot remove the organization creator');
    }
    
    // Remove the member
    const updatedMembers = [...members];
    updatedMembers.splice(memberIndex, 1);
    
    await updateDoc(doc(db, 'organizations', orgId), {
      members: updatedMembers,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error removing organization member:', error);
    throw error;
  }
};

/**
 * Create a ticket type for an event
 */
export const createEventTicket = async (
  eventId: string,
  orgId: string,
  ticketData: Omit<Ticket, 'id' | 'eventId' | 'soldCount' | 'status'>
): Promise<string> => {
  try {
    // Create ticket object
    const ticket: Omit<Ticket, 'id'> = {
      eventId,
      organizationId: orgId,
      name: ticketData.name,
      description: ticketData.description,
      type: ticketData.type,
      price: ticketData.price,
      currency: ticketData.currency,
      quantity: ticketData.quantity,
      available: ticketData.quantity, // Same as quantity initially
      soldCount: 0,
      maxPerPurchase: ticketData.maxPerPurchase,
      minPerPurchase: ticketData.minPerPurchase,
      startSales: ticketData.startSales || ticketData.saleStart || new Date(),
      endSales: ticketData.endSales || ticketData.saleEnd || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days from now by default
      saleStart: ticketData.saleStart,
      saleEnd: ticketData.saleEnd,
      status: 'active',
      settings: ticketData.settings,
      metadata: ticketData.metadata
    };
    
    // Add ticket to Firestore
    const docRef = await addDoc(collection(db, 'eventTickets'), ticket);
    
    // Also update the event to reference this ticket
    const eventRef = doc(db, 'events', eventId);
    await updateDoc(eventRef, {
      hasTickets: true,
      ticketTypes: arrayUnion(docRef.id),
      updatedAt: serverTimestamp()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error creating event ticket:', error);
    throw error;
  }
};

/**
 * Get tickets for an event
 */
export const getEventTickets = async (eventId: string): Promise<Ticket[]> => {
  try {
    const ticketsQuery = query(
      collection(db, 'eventTickets'),
      where('eventId', '==', eventId),
      orderBy('price')
    );
    
    const ticketsSnapshot = await getDocs(ticketsQuery);
    
    const tickets: Ticket[] = [];
    ticketsSnapshot.forEach(doc => {
      tickets.push({ id: doc.id, ...doc.data() } as Ticket);
    });
    
    return tickets;
  } catch (error) {
    console.error('Error getting event tickets:', error);
    throw error;
  }
};

/**
 * Update a ticket
 */
export const updateEventTicket = async (
  ticketId: string,
  data: Partial<Ticket>
): Promise<void> => {
  try {
    await updateDoc(doc(db, 'eventTickets', ticketId), {
      ...data,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating event ticket:', error);
    throw error;
  }
};

/**
 * Delete a ticket
 */
export const deleteEventTicket = async (
  ticketId: string,
  eventId: string
): Promise<void> => {
  try {
    // Delete the ticket
    await deleteDoc(doc(db, 'eventTickets', ticketId));
    
    // Update the event to remove this ticket reference
    const eventRef = doc(db, 'events', eventId);
    await updateDoc(eventRef, {
      ticketTypes: arrayRemove(ticketId),
      updatedAt: serverTimestamp()
    });
    
    // Check if this was the last ticket and update hasTickets flag if needed
    const remainingTicketsQuery = query(
      collection(db, 'eventTickets'),
      where('eventId', '==', eventId),
      limit(1)
    );
    
    const ticketsSnapshot = await getDocs(remainingTicketsQuery);
    
    if (ticketsSnapshot.empty) {
      await updateDoc(eventRef, {
        hasTickets: false
      });
    }
  } catch (error) {
    console.error('Error deleting event ticket:', error);
    throw error;
  }
};

/**
 * Get public events for an organization (for marketing/discovery)
 */
export const getOrganizationPublicEvents = async (orgId: string): Promise<any[]> => {
  try {
    const eventsQuery = query(
      collection(db, 'events'),
      where('organizationId', '==', orgId),
      where('isPublic', '==', true),
      where('startDate', '>=', new Date()),
      orderBy('startDate')
    );
    
    const eventsSnapshot = await getDocs(eventsQuery);
    
    const events: any[] = [];
    eventsSnapshot.forEach(doc => {
      events.push({ id: doc.id, ...doc.data() });
    });
    
    return events;
  } catch (error) {
    console.error('Error getting organization public events:', error);
    throw error;
  }
};

/**
 * Process a ticket purchase (placeholder - will need integration with payment processor)
 */
export const processTicketPurchase = async (
  ticketId: string,
  eventId: string,
  buyerUserId: string,
  quantity: number,
  paymentDetails: any
): Promise<string> => {
  try {
    // Get the ticket details 
    const ticketDoc = await getDoc(doc(db, 'eventTickets', ticketId));
    
    if (!ticketDoc.exists()) {
      throw new Error('Ticket not found');
    }
    
    const ticketData = ticketDoc.data() as Ticket;
    const soldCount = ticketData.soldCount || 0;
    
    // Check if enough tickets are available
    if (soldCount + quantity > ticketData.quantity) {
      throw new Error('Not enough tickets available');
    }
    
    // Create a purchase record
    const purchaseData = {
      ticketId,
      eventId,
      buyerId: buyerUserId,
      quantity,
      unitPrice: ticketData.price,
      totalAmount: ticketData.price * quantity,
      currency: ticketData.currency,
      purchaseDate: serverTimestamp(),
      status: 'completed', // In reality, this would start as 'pending'
      paymentDetails, // This would contain payment processor-specific details
      ticketCode: Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    };
    
    const purchaseRef = await addDoc(collection(db, 'ticketPurchases'), purchaseData);
    
    // Update the ticket sold count
    await updateDoc(doc(db, 'eventTickets', ticketId), {
      soldCount: soldCount + quantity,
      updatedAt: serverTimestamp()
    });
    
    // If this purchase would sell out the ticket, update its status
    if (soldCount + quantity >= ticketData.quantity) {
      await updateDoc(doc(db, 'eventTickets', ticketId), {
        status: 'sold_out'
      });
    }
    
    return purchaseRef.id;
  } catch (error) {
    console.error('Error processing ticket purchase:', error);
    throw error;
  }
};

/**
 * Create a ticket type for an event
 */
export const createTicket = async (
  organizationId: string,
  eventId: string,
  ticketData: Omit<Ticket, 'id' | 'status' | 'eventId' | 'organizationId'>
): Promise<string> => {
  try {
    // Create ticket document
    const ticketRef = doc(collection(db, 'eventTickets'));
    
    const newTicket: Omit<Ticket, 'id'> = {
      eventId,
      organizationId,
      type: ticketData.type,
      name: ticketData.name,
      description: ticketData.description,
      price: ticketData.price,
      currency: ticketData.currency,
      quantity: ticketData.quantity,
      available: ticketData.quantity, // Initially all tickets are available
      startSales: ticketData.startSales || ticketData.saleStart, // Support both properties
      endSales: ticketData.endSales || ticketData.saleEnd, // Support both properties
      maxPerOrder: ticketData.maxPerOrder || ticketData.maxPerPurchase || 10, // Default to 10
    };
    
    // Add ticket to Firestore
    await setDoc(ticketRef, {
      ...newTicket,
      id: ticketRef.id,
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return ticketRef.id;
  } catch (error) {
    console.error('Error creating ticket:', error);
    throw new Error('Failed to create ticket');
  }
};

/**
 * Purchase a ticket
 */
export const purchaseTicket = async (
  ticketId: string,
  userId: string,
  quantity: number,
  paymentData: any
): Promise<string> => {
  try {
    // Get ticket data
    const ticketDoc = await getDoc(doc(db, 'eventTickets', ticketId));
    if (!ticketDoc.exists()) {
      throw new Error('Ticket not found');
    }
    
    const ticketData = ticketDoc.data() as Ticket;
    
    // Check if ticket is on sale
    const now = new Date();
    const startDate = ticketData.startSales instanceof Date 
      ? ticketData.startSales 
      : new Date(ticketData.startSales?.seconds * 1000);
    const endDate = ticketData.endSales instanceof Date
      ? ticketData.endSales
      : new Date(ticketData.endSales?.seconds * 1000);
    
    if (now < startDate || now > endDate) {
      throw new Error('Ticket is not currently on sale');
    }
    
    // Check if enough tickets are available
    const soldCount = ticketData.soldCount || 0;
    if (soldCount + quantity > ticketData.quantity) {
      throw new Error('Not enough tickets available');
    }
    
    // Process payment (this would integrate with a payment processor)
    // ... payment processing code ...
    
    // Create purchase record
    const purchaseRef = doc(collection(db, 'ticketPurchases'));
    await setDoc(purchaseRef, {
      id: purchaseRef.id,
      ticketId,
      eventId: ticketData.eventId,
      organizationId: ticketData.organizationId,
      userId,
      quantity,
      unitPrice: ticketData.price,
      totalPrice: ticketData.price * quantity,
      currency: ticketData.currency,
      paymentId: paymentData.paymentId || 'mock-payment-id',
      status: 'completed',
      purchasedAt: serverTimestamp()
    });
    
    // Update the ticket sold count
    await updateDoc(doc(db, 'eventTickets', ticketId), {
      soldCount: soldCount + quantity,
      updatedAt: serverTimestamp()
    });
    
    // If this purchase would sell out the ticket, update its status
    if (soldCount + quantity >= ticketData.quantity) {
      await updateDoc(doc(db, 'eventTickets', ticketId), {
        status: 'sold_out'
      });
    }
    
    return purchaseRef.id;
  } catch (error) {
    console.error('Error purchasing ticket:', error);
    throw error;
  }
};

/**
 * Fetch metrics for an organization dashboard
 */
export const fetchOrganizationMetrics = async (organizationId: string): Promise<any> => {
  try {
    // Get organization data (to get member count)
    const orgData = await getOrganization(organizationId);
    if (!orgData) {
      throw new Error('Organization not found');
    }
    
    // Get events for this organization
    const eventsQuery = query(
      collection(db, 'events'),
      where('organizationId', '==', organizationId)
    );
    const eventsSnapshot = await getDocs(eventsQuery);
    const allEvents: any[] = eventsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter for upcoming events
    const now = new Date();
    const upcomingEvents = allEvents.filter((event: any) => {
      if (!event.startDate) return false;
      
      const eventDate = event.startDate instanceof Date 
        ? event.startDate 
        : event.startDate.toDate ? event.startDate.toDate() : new Date(event.startDate);
        
      return eventDate > now;
    });
    
    // Count trips
    const tripsQuery = query(
      collection(db, 'trips'),
      where('organizationId', '==', organizationId)
    );
    const tripsSnapshot = await getDocs(tripsQuery);
    
    // Count photos
    const photosQuery = query(
      collection(db, 'photos'),
      where('organizationId', '==', organizationId)
    );
    const photosSnapshot = await getDocs(photosQuery);
    
    // Get all invitations for this organization's events
    const invitationsQuery = query(
      collection(db, 'eventInvitations'),
      where('organizationId', '==', organizationId)
    );
    const invitationsSnapshot = await getDocs(invitationsQuery);
    const allInvitations: any[] = invitationsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Count accepted invitations
    const acceptedInvitations = allInvitations.filter((invite: any) => 
      invite.status === 'accepted'
    );
    
    // Calculate storage used (actual implementation would query storage usage)
    // This is a placeholder - in reality would need to sum sizes of all organization files
    const storageUsed = photosSnapshot.size * 5 * 1024 * 1024; // Rough average per photo (upload cap is higher)
    
    return {
      totalEvents: eventsSnapshot.size,
      upcomingEvents: upcomingEvents.length,
      totalMembers: orgData.members.length,
      totalTrips: tripsSnapshot.size,
      totalPhotos: photosSnapshot.size,
      totalInvitations: invitationsSnapshot.size,
      acceptedInvitations: acceptedInvitations.length,
      storageUsed
    };
  } catch (error) {
    console.error('Error fetching organization metrics:', error);
    // Return default values in case of error
    return {
      totalEvents: 0,
      upcomingEvents: 0,
      totalMembers: 0,
      totalTrips: 0,
      totalPhotos: 0,
      totalInvitations: 0,
      acceptedInvitations: 0,
      storageUsed: 0
    };
  }
}; 